import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, wsURL, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Eye, Lock, KeyRound, ExternalLink } from "lucide-react";
import { toast } from "sonner";

function SecretMessage({ m, isSelf }) {
  const [pw, setPw] = useState("");
  const [revealed, setRevealed] = useState(null);
  const [open, setOpen] = useState(false);

  const unlock = async () => {
    try {
      const { data } = await api.post("/credentials/reveal", { credential_id: m.credential_id, password_confirm: pw });
      setRevealed(data);
      toast.success("Vault opened");
    } catch (e) {
      toast.error(errMsg(e, "Re-auth failed"));
    }
  };

  return (
    <div className={`flex ${isSelf ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%] rounded-2xl px-4 py-3 text-sm bg-gradient-to-br from-violet-500/15 to-cyan-500/10 border border-violet-400/25"
        data-testid={`chat-msg-credential-${m.message_id}`}>
        <div className="text-[10px] uppercase tracking-widest opacity-70 mb-1">{m.sender_name} · {m.sender_role}</div>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-violet-500/20 flex items-center justify-center shrink-0">
            <KeyRound size={16} className="text-violet-200"/>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold">Sealed vault</div>
            <div className="text-xs opacity-80">{m.label}</div>
            {m.url && (
              <a href={m.url} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-cyan-200 inline-flex items-center gap-1 mt-1">
                {m.url} <ExternalLink size={10}/>
              </a>
            )}
          </div>
          {!revealed && (
            <button onClick={()=>setOpen(v=>!v)} className="pill-btn ghost text-xs px-3 py-1.5"
              data-testid={`chat-cred-unlock-${m.credential_id}`}>
              <Lock size={12}/> Unlock
            </button>
          )}
        </div>
        {open && !revealed && (
          <div className="mt-3 flex gap-2">
            <input type="password" value={pw} onChange={e=>setPw(e.target.value)}
              placeholder="Confirm password / OTP"
              data-testid={`chat-cred-pw-${m.credential_id}`}
              className="flex-1 bg-black/40 hairline-strong rounded-lg px-3 py-2 text-sm outline-none focus:border-violet-500"/>
            <button onClick={unlock} className="pill-btn primary text-xs px-3" data-testid={`chat-cred-reveal-${m.credential_id}`}>
              <Eye size={12}/> Reveal
            </button>
          </div>
        )}
        {revealed && (
          <div className="mt-3 grid gap-1 font-mono text-sm bg-black/40 rounded-lg p-3">
            <div>user: <span className="text-cyan-300">{revealed.username}</span></div>
            <div>pass: <span className="text-cyan-300">{revealed.password}</span></div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatPanel({ projectId }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/chat/${projectId}`).then(r => setMsgs(r.data));
    const token = localStorage.getItem("craftora_token") || "";
    const ws = new WebSocket(wsURL(projectId, token));
    ws.onmessage = e => { try { setMsgs(prev => [...prev, JSON.parse(e.data)]); } catch(_){} };
    ws.onerror = () => {};
    wsRef.current = ws;
    return () => ws.close();
  }, [projectId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      await api.post("/chat/send", { project_id: projectId, content: text });
      setText("");
    } catch (e) { toast.error(errMsg(e, "Send failed")); }
  };

  return (
    <div className="glass rounded-2xl flex flex-col h-[520px]" data-testid="chat-panel">
      <div className="p-4 border-b border-white/[0.06] label-mono">Project chat</div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {msgs.map(m => {
          const isSelf = m.sender_id === user.user_id;
          if (m.type === "credential") return <SecretMessage key={m.message_id} m={m} isSelf={isSelf}/>;
          return (
            <div key={m.message_id} className={`flex ${isSelf ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${isSelf ? "bg-violet-500 text-white" : "bg-white/5 hairline"}`}>
                <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name} · {m.sender_role}</div>
                {typeof m.content === "string" ? m.content : JSON.stringify(m.content)}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      <div className="p-3 border-t border-white/[0.06] flex gap-2">
        <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&send()}
          placeholder="Type a message…"
          data-testid="chat-input"
          className="flex-1 bg-transparent hairline-strong rounded-lg px-3 py-2 outline-none focus:border-violet-500 text-sm"/>
        <button onClick={send} className="pill-btn violet px-3" data-testid="chat-send"><Send size={14}/></button>
      </div>
    </div>
  );
}

function CredentialsPanel({ projectId }) {
  const [creds, setCreds] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [pw, setPw] = useState({});

  const load = useCallback(async () => {
    if (!projectId) return;

    const { data } = await api.get(`/credentials/${projectId}`);
    setCreds(data);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const reveal = async (c) => {
    try {
      const { data } = await api.post("/credentials/reveal", { credential_id: c.credential_id, password_confirm: pw[c.credential_id] || "" });
      setRevealed(prev => ({...prev, [c.credential_id]: data}));
      toast.success("Credentials revealed");
    } catch (e) {
      toast.error(errMsg(e, "Re-auth failed"));
    }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="credentials-panel">
      <div className="flex items-center justify-between">
        <div className="label-mono">Secure credentials</div>
        <Lock size={14} className="text-white/40"/>
      </div>
      {creds.length === 0 && <div className="text-white/50 text-sm mt-4">No credentials delivered yet.</div>}
      <div className="mt-4 space-y-3">
        {creds.map(c => (
          <div key={c.credential_id} className="hairline rounded-xl p-4" data-testid={`cred-${c.credential_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{c.label}</div>
                {c.url && <div className="text-xs text-white/50 font-mono">{c.url}</div>}
              </div>
              {c.revealed_at ? <span className="chip emerald">Viewed</span> : <span className="chip amber">Sealed</span>}
            </div>
            {revealed[c.credential_id] ? (
              <div className="mt-3 grid gap-1 font-mono text-sm">
                <div>user: <span className="text-cyan-300">{revealed[c.credential_id].username}</span></div>
                <div>pass: <span className="text-cyan-300">{revealed[c.credential_id].password}</span></div>
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <input type="password" value={pw[c.credential_id]||""} onChange={e=>setPw({...pw,[c.credential_id]:e.target.value})}
                  placeholder="Confirm password / OTP"
                  data-testid={`cred-pw-${c.credential_id}`}
                  className="flex-1 bg-transparent hairline-strong rounded-lg px-3 py-2 text-sm outline-none focus:border-violet-500"/>
                <button onClick={()=>reveal(c)} className="pill-btn ghost text-sm px-3" data-testid={`cred-reveal-${c.credential_id}`}>
                  <Eye size={14}/> Reveal
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/orders/mine").then(r => {
      setOrders(r.data);
      if (r.data.length) setActive(r.data[0]);
    });
  }, []);

  const statusColor = (s) => ({new:"chip", in_progress:"chip cyan", review:"chip amber", delivered:"chip emerald"}[s]||"chip");

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="client-dashboard">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono mb-2">Client workspace</div>
          <h1 className="font-display font-black text-4xl">Hi, {user?.name?.split(" ")[0]}.</h1>
        </div>
        <a href="/order" className="pill-btn primary" data-testid="new-order-btn">Start new project</a>
      </div>

      <div className="grid lg:grid-cols-12 gap-6 mt-10">
        <aside className="lg:col-span-4 space-y-3">
          <div className="label-mono">Your projects</div>
          {orders.length===0 && <div className="glass rounded-xl p-4 text-white/50 text-sm">No projects yet.</div>}
          {orders.map(o => (
            <button key={o.order_id} onClick={()=>setActive(o)}
              data-testid={`order-${o.order_id}`}
              className={`w-full text-left glass rounded-xl p-4 transition-colors ${active?.order_id===o.order_id?"ring-1 ring-violet-500/40":""}`}>
              <div className="flex items-center justify-between">
                <div className="font-display font-bold">{o.business_name}</div>
                <span className={statusColor(o.status)}>{o.status.replace('_',' ')}</span>
              </div>
              <div className="text-xs text-white/50 mt-1 font-mono">#{o.order_id}</div>
              <div className="text-sm text-white/60 mt-2">{o.package.toUpperCase()} · {inr(o.price)}</div>
            </button>
          ))}
        </aside>

        <section className="lg:col-span-8 space-y-6">
          {active ? (
            <>
              <div className="glass rounded-2xl p-6" data-testid="project-detail">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="label-mono">Project</div>
                    <h2 className="font-display font-black text-2xl mt-1">{active.business_name}</h2>
                  </div>
                  <span className={statusColor(active.status)}>{active.status.replace('_',' ')}</span>
                </div>
                <div className="mt-4 grid md:grid-cols-3 gap-3 text-sm text-white/70">
                  <div><div className="label-mono mb-1">Category</div>{active.category}</div>
                  <div><div className="label-mono mb-1">Style</div>{active.style}</div>
                  <div><div className="label-mono mb-1">Package</div>{active.package}</div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <ChatPanel projectId={active.project_id}/>
                <CredentialsPanel projectId={active.project_id}/>
              </div>
            </>
          ) : (
            <div className="glass rounded-2xl p-10 text-center text-white/60">Start your first project to unlock chat and deliverables.</div>
          )}
        </section>
      </div>
    </div>
  );
}
